import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, LogOut, CreditCard, Rocket, MailWarning } from "lucide-react";
import { API } from "../config/env";
import { useAuth } from "../context/AuthContext";
import { loadRazorpayScript } from "../lib/razorpay";
import { getProductBySku, buildQuotePrefill } from "../config/products";
import { getServiceById } from "../config/services";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function PortalPage() {
  const { user, loading, isAuthenticated, logout } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [payingQuoteId, setPayingQuoteId] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    axios.get(`${API}/portal/quotes`).then(r => setQuotes(r.data)).catch(()=>{});
    axios.get(`${API}/portal/orders`).then(r => setOrders(r.data)).catch(()=>{});
    axios.get(`${API}/portal/projects`).then(r => setProjects(r.data)).catch(()=>{});
  }, [user, isAuthenticated, refreshKey]);

  const handlePayAdvance = async (quote) => {
    setPayingQuoteId(quote.id);
    try {
      const { data: payOrder } = await axios.post(`${API}/payment/quote/create-order`, { quote_id: quote.id });
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Could not load Razorpay checkout. Please try again.");
        setPayingQuoteId(null);
        return;
      }
      const rzp = new window.Razorpay({
        key: payOrder.key_id,
        amount: payOrder.amount,
        currency: payOrder.currency,
        order_id: payOrder.razorpay_order_id,
        name: "DEBSUN Technologies",
        description: `Advance payment · ${payOrder.reference}`,
        prefill: payOrder.prefill,
        theme: { color: "#D4AF37" },
        handler: async (response) => {
          try {
            await axios.post(`${API}/payment/quote/verify`, {
              quote_id: quote.id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Advance paid. Project created.");
            setRefreshKey((k) => k + 1);
          } catch {
            toast.error("Payment verification failed. Please contact support.");
          } finally {
            setPayingQuoteId(null);
          }
        },
        modal: { ondismiss: () => setPayingQuoteId(null) },
      });
      rzp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
        setPayingQuoteId(null);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment.");
      setPayingQuoteId(null);
    }
  };

  const [quotePrefill, setQuotePrefill] = useState(null);
  useEffect(() => {
    if (!isAuthenticated) return;

    // URL handoff from Products / Services (quote=<sku> | service=<id>)
    const params = new URLSearchParams(window.location.search);
    const quoteSku = params.get("quote");
    const serviceId = params.get("service");
    if (quoteSku || serviceId) {
      let prefill = null;
      if (quoteSku) {
        const product = getProductBySku(quoteSku);
        if (product) prefill = buildQuotePrefill(product);
      } else if (serviceId) {
        const service = getServiceById(serviceId);
        if (service) {
          prefill = {
            project_name: `${service.name} engagement`,
            industry: (service.industries && service.industries[0]) || "",
            notes: `Service: ${service.name}`,
            items: [{ description: service.name, quantity: 1 }],
          };
        }
      }
      if (prefill) setQuotePrefill(prefill);
      // Clean query params without reload
      const url = new URL(window.location.href);
      url.searchParams.delete("quote");
      url.searchParams.delete("service");
      window.history.replaceState({}, "", url.pathname + url.search);
      return;
    }

    if (sessionStorage.getItem("debsun-open-quote") !== "1") return;
    const raw = sessionStorage.getItem("debsun-quote-prefill");
    sessionStorage.removeItem("debsun-open-quote");
    sessionStorage.removeItem("debsun-quote-prefill");
    if (raw) {
      try { setQuotePrefill(JSON.parse(raw)); } catch (e) { /* ignore malformed prefill */ }
    }
  }, [user, isAuthenticated]);

  if (loading) return <div className="pt-40 text-center text-muted-foreground" data-testid="portal-auth-loading">Loading…</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen pt-28 pb-20" data-testid="portal-page">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">
              Client Portal · v1.0
            </div>
            <h1 className="display display-section text-4xl md:text-5xl tracking-[-0.03em]">
              Welcome, <em className="italic text-[color:var(--gold)]">{user.name?.split(" ")[0]}</em>.
            </h1>
            <div className="mt-2 text-sm text-muted-foreground">{user.company || "—"} · {user.email}</div>
          </div>
          <Button onClick={logout} variant="outline" className="rounded-sm" data-testid="portal-logout">
            <LogOut size={14} /> Logout
          </Button>
        </div>

        {user.email_verified === false && (
          <VerifyBanner />
        )}

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Active quotes" value={quotes.filter(q => !["closed", "lost"].includes(q.status)).length} accent="var(--gold)" />
          <StatCard label="Orders placed" value={orders.length} accent="var(--teal)" />
          <StatCard label="Projects created" value={projects.length} accent="var(--purple)" />
        </div>

        <Tabs defaultValue="quotes" className="mt-12">
          <TabsList className="rounded-sm">
            <TabsTrigger value="quotes" data-testid="tab-quotes">Quote Requests</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Order Tracking</TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">My Projects</TabsTrigger>
          </TabsList>

          <TabsContent value="quotes" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">§ Your quotes</div>
              <NewQuoteDialog onCreated={() => setRefreshKey(k=>k+1)} initialForm={quotePrefill} />
            </div>
            {quotes.length === 0 ? (
              <EmptyRow msg="No quote requests yet. Create your first one." />
            ) : (
              <div className="space-y-3">
                {quotes.map((q) => (
                  <div key={q.id} data-testid={`quote-row-${q.reference}`}
                       className="p-5 clip-frame-sm border border-border bg-background flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-[color:var(--gold)]">{q.reference}</div>
                      <div className="mt-1 display text-xl">{q.project_name}</div>
                      <div className="text-sm text-muted-foreground mt-1">Industry: {q.industry || "—"} · Items: {q.items?.length || 0}</div>
                      {q.notes && <div className="text-xs text-muted-foreground mt-2 max-w-xl">{q.notes}</div>}

                      {q.status === "quoted" && q.advance_amount > 0 && (
                        <div className="mt-4 p-4 border border-[color:var(--gold)]/40 bg-[color:var(--gold)]/5" data-testid={`quote-advance-panel-${q.reference}`}>
                          <div className="font-mono-2 text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Advance payment required</div>
                          <div className="display text-2xl mt-1" data-testid={`quote-advance-amount-${q.reference}`}>{money(q.advance_amount)}</div>
                          <Button
                            onClick={() => handlePayAdvance(q)}
                            disabled={payingQuoteId === q.id}
                            className="btn-gold rounded-none clip-frame-sm mt-3 h-10 px-5 text-xs uppercase tracking-widest font-mono-2"
                            data-testid={`quote-pay-advance-${q.reference}`}
                          >
                            <CreditCard size={14} /> {payingQuoteId === q.id ? "Processing…" : "Pay Advance with Razorpay"}
                          </Button>
                        </div>
                      )}

                      {q.status === "advance_paid" && (
                        <div className="mt-4 p-4 border border-[color:var(--teal)]/40 bg-[color:var(--teal)]/5 flex items-center gap-3" data-testid={`quote-project-created-${q.reference}`}>
                          <Rocket size={18} className="text-[color:var(--teal)]" />
                          <div>
                            <div className="text-sm">Advance of {money(q.advance_amount)} paid — project created.</div>
                            <div className="font-mono-2 text-[10px] tracking-[0.25em] uppercase text-[color:var(--teal)] mt-1">{q.project_reference}</div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Status</div>
                      <div className="mt-1 inline-flex px-3 py-1 border border-[color:var(--gold)]/50 rounded-sm text-xs font-mono-2 uppercase tracking-widest text-[color:var(--gold)]">
                        {q.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">§ Orders in motion</div>
            {orders.length === 0 ? (
              <EmptyRow msg="No orders yet. Browse the shop to place your first order." />
            ) : (
              <div className="space-y-6">
                {orders.map((o) => (
                  <motion.div key={o.id} initial={{opacity:0, y:15}} animate={{opacity:1, y:0}}
                              transition={{duration:.5}}
                              className="p-6 clip-frame-sm border border-border bg-background"
                              data-testid={`order-row-${o.order_no}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-[color:var(--gold)]">{o.order_no}</div>
                        <div className="mt-1 display text-2xl leading-tight">
                          {o.items?.map((it) => `${it.name} × ${it.quantity}`).join(", ")}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">Amount: {money(o.amount)}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Payment</div>
                        <div className={`mt-1 inline-flex px-3 py-1 border rounded-sm text-xs font-mono-2 uppercase tracking-widest ${
                          o.payment_status === "paid" ? "border-[color:var(--teal)]/50 text-[color:var(--teal)]" : "border-[color:var(--gold)]/50 text-[color:var(--gold)]"
                        }`}>
                          {o.payment_status === "paid" ? "Paid" : "Pending"}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="projects" className="mt-6">
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">§ Projects created from advance payments</div>
            {projects.length === 0 ? (
              <EmptyRow msg="No projects yet. Pay an advance on a quoted request to create your first project." />
            ) : (
              <div className="space-y-4">
                {projects.map((p) => (
                  <motion.div key={p.id} initial={{opacity:0, y:15}} animate={{opacity:1, y:0}}
                              transition={{duration:.5}}
                              className="p-6 clip-frame-sm border border-border bg-background flex items-start justify-between gap-4 flex-wrap"
                              data-testid={`project-row-${p.reference}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-sm border border-[color:var(--teal)]/40 flex items-center justify-center shrink-0">
                        <Rocket size={18} className="text-[color:var(--teal)]" />
                      </div>
                      <div>
                        <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-[color:var(--teal)]">{p.reference}</div>
                        <div className="mt-1 display text-xl">{p.project_name}</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Advance paid: {money(p.advance_amount)}{p.quote_reference ? ` · from ${p.quote_reference}` : ""}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Status</div>
                      <div className="mt-1 display text-lg capitalize">{p.status}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="p-6 clip-frame border border-border bg-background relative overflow-hidden" data-testid={`stat-${label.replace(/\s+/g,'-').toLowerCase()}`}>
      <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">{label}</div>
      <div className="mt-4 display text-6xl leading-none" style={{ color: accent }}>{value}</div>
      <div className="hairline mt-6" />
    </div>
  );
}

function EmptyRow({ msg }) {
  return <div className="p-10 border border-dashed border-border text-center text-sm text-muted-foreground rounded-sm">{msg}</div>;
}

function VerifyBanner() {
  const { resendVerification } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResend = async () => {
    setSending(true);
    try {
      await resendVerification();
      setSent(true);
      toast.success("Verification email sent — check your inbox.");
    } catch {
      toast.error("Could not send verification email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-8 p-4 border border-[color:var(--gold)]/40 bg-[color:var(--gold)]/5 flex items-center justify-between gap-4 flex-wrap"
         data-testid="verify-email-banner">
      <div className="flex items-center gap-3">
        <MailWarning size={18} className="text-[color:var(--gold)] shrink-0" />
        <div className="text-sm">Please verify your email address to activate full account access.</div>
      </div>
      <Button
        onClick={handleResend}
        disabled={sending || sent}
        variant="outline"
        className="rounded-sm h-9 text-xs uppercase tracking-widest font-mono-2"
        data-testid="resend-verification-btn"
      >
        {sent ? "Sent" : sending ? "Sending…" : "Resend Verification Email"}
      </Button>
    </div>
  );
}

function NewQuoteDialog({ onCreated, initialForm }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_name: "", industry: "", notes: "", items: [{ description: "", quantity: 1 }] });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (initialForm) {
      setForm((f) => ({ ...f, ...initialForm }));
      setOpen(true);
    }
  }, [initialForm]);

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/portal/quotes`, form);
      toast.success("Quote request submitted. We'll respond within 1 business day.");
      setOpen(false);
      setForm({ project_name: "", industry: "", notes: "", items: [{ description: "", quantity: 1 }] });
      onCreated?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((x) => x?.msg).filter(Boolean).join(" ") : null;
      toast.error(msg || "Could not submit. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-gold rounded-sm uppercase font-mono-2 text-xs tracking-widest" data-testid="new-quote-btn">
          <Plus size={14} /> New Quote
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader>
          <DialogTitle className="display display-section text-2xl">New quote request</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Project name" value={form.project_name} onChange={set("project_name")} data-testid="quote-project" className="rounded-sm h-11" />
          <Input placeholder="Industry (e.g. steel, manufacturing)" value={form.industry} onChange={set("industry")} data-testid="quote-industry" className="rounded-sm h-11" />
          <Textarea rows={3} placeholder="Item description (e.g. 40 highbays 150W for 12k sqm warehouse)"
                    value={form.items[0].description}
                    onChange={(e) => setForm(f => ({ ...f, items: [{ ...f.items[0], description: e.target.value }] }))}
                    data-testid="quote-item" className="rounded-sm" />
          <Textarea rows={3} placeholder="Additional notes (location, timeline, drawings…)"
                    value={form.notes} onChange={set("notes")} data-testid="quote-notes" className="rounded-sm" />
        </div>
        <Button onClick={submit} disabled={loading || !form.project_name} className="btn-gold rounded-sm w-full h-11" data-testid="quote-submit">
          {loading ? "Submitting…" : "Submit request"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
